/**
 * SEO утилиттері
 * Беттің мета тегтерін басқаруға арналған функциялар
 */

import i18next from 'i18next';
import { generateSlug } from './slugify';

const BASE_URL = 'https://agroland.kz';

const LANG_LOCALE = {
  kz: 'kk_KZ',
  ru: 'ru_KZ',
  en: 'en_US',
  cn: 'zh_CN',
};

const LANG_HTML = {
  kz: 'kk',
  ru: 'ru',
  en: 'en',
  cn: 'zh',
};

function currentLang(lang = null) {
  if (lang) return lang;
  return i18next.language || localStorage.getItem('language') || 'kz';
}

export function setHTMLLang(lang = null) {
  const l = currentLang(lang);
  const htmlLang = LANG_HTML[l] || 'kk';
  if (document.documentElement) document.documentElement.lang = htmlLang;
}

function langImage(lang = null) {
  const l = currentLang(lang);
  const suffix = ['kz', 'ru', 'en', 'cn'].includes(l) ? l : 'kz';
  return `/top-promo-${suffix}.png`;
}

/**
 * Беттің тақырыбын орнату
 * @param {string} title - Беттің атауы
 * @param {string} siteName - Сайттың атауы (қосымша)
 */
export function setTitle(title, siteName = 'Agroland.kz') {
  if (title && siteName) {
    const suffix = ` — ${siteName}`;
    const clean = title.endsWith(suffix) ? title.slice(0, -suffix.length) : title;
    document.title = `${clean} — ${siteName}`;
  } else if (title) {
    document.title = title;
  } else {
    document.title = siteName;
  }
}

/**
 * Meta тегті орнату немесе жаңарту
 * @param {string} name - Meta тегтің атауы
 * @param {string} content - Meta тегтің мәні
 * @param {string} type - 'name' немесе 'property'
 */
function setMetaTag(name, content, type = 'name') {
  let meta = document.querySelector(`meta[${type}="${name}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(type, name);
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
}

/**
 * Link тегті орнату (canonical, alternate, etc.)
 * @param {string} rel - Rel атрибуты
 * @param {string} href - Href атрибуты
 */
function setLinkTag(rel, href) {
  let link = document.querySelector(`link[rel="${rel}"]`);

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}

/**
 * Structured Data (JSON-LD) орнату
 * @param {Object} data - Schema.org деректері
 */
export function setStructuredData(data, schemaId = 'default') {
  let script = document.querySelector(`script[type="application/ld+json"][data-schema-id="${schemaId}"]`);

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema-id', schemaId);
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

export function removeStructuredData(schemaId) {
  const script = document.querySelector(`script[type="application/ld+json"][data-schema-id="${schemaId}"]`);
  if (script) {
    script.remove();
  }
}

export function resetSEO() {
  const currentPath = window.location.pathname;
  const fullUrl = `${BASE_URL}${currentPath}`;
  const lang = currentLang();
  const locale = LANG_LOCALE[lang] || 'kk_KZ';

  setHTMLLang(lang);
  setTitle('Agroland.kz');
  setMetaTag('description', 'Agroland.kz — Agricultural marketplace in Kazakhstan');
  setMetaTag('keywords', 'agriculture, agro, machinery, seeds, fertilizers, Kazakhstan');
  setMetaTag('robots', 'index, follow');
  setMetaTag('author', 'Agroland');
  setLinkTag('canonical', fullUrl);
  setMetaTag('og:type', 'website', 'property');
  setMetaTag('og:url', fullUrl, 'property');
  setMetaTag('og:title', 'Agroland.kz', 'property');
  setMetaTag('og:description', 'Ауыл шаруашылығы тауарларының нарығы', 'property');
  setMetaTag('og:image', `${BASE_URL}${langImage(lang)}`, 'property');
  setMetaTag('og:locale', locale, 'property');
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:url', fullUrl, 'name');
  setMetaTag('twitter:title', 'Agroland.kz', 'name');
  setMetaTag('twitter:description', 'Ауыл шаруашылығы тауарларының нарығы', 'name');
  setMetaTag('twitter:image', `${BASE_URL}${langImage(lang)}`, 'name');
  removeStructuredData('page');
  removeStructuredData('breadcrumb');
}

/**
 * Барлық SEO мета тегтерін орнату
 * @param {Object} options - SEO параметрлері
 * @param {string} options.title - Беттің атауы
 * @param {string} options.description - Беттің сипаттамасы
 * @param {string} options.keywords - Кілт сөздер
 * @param {string} options.robots - Robots директивасы (index/noindex)
 * @param {string} options.canonical - Canonical URL
 * @param {string} options.ogImage - Open Graph суреті
 * @param {string} options.ogType - Open Graph түрі (website, article, product)
 * @param {Object} options.structuredData - Structured data объектісі
 */
export function setSEO(options = {}) {
  const {
    title,
    description,
    keywords,
    robots = 'index, follow',
    canonical,
    ogImage,
    ogType = 'website',
    structuredData,
  } = options;

  const currentPath = window.location.pathname;
  const fullUrl = canonical || `${BASE_URL}${currentPath}`;
  const lang = currentLang();
  const locale = LANG_LOCALE[lang] || 'kk_KZ';
  const image = ogImage ? (ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`) : `${BASE_URL}${langImage(lang)}`;

  setHTMLLang(lang);

  // Беттің тақырыбы
  setTitle(title);

  // Primary Meta Tags
  setMetaTag('description', description || 'Agroland.kz — Agricultural marketplace in Kazakhstan');
  setMetaTag('keywords', keywords || 'agriculture, agro, machinery, seeds, fertilizers, Kazakhstan');
  setMetaTag('robots', robots);
  setMetaTag('author', 'Agroland');

  // Canonical URL
  setLinkTag('canonical', fullUrl);

  // Open Graph
  setMetaTag('og:type', ogType, 'property');
  setMetaTag('og:url', fullUrl, 'property');
  setMetaTag('og:title', title || 'Agroland.kz', 'property');
  setMetaTag('og:description', description || 'Ауыл шаруашылығы тауарларының нарығы', 'property');
  setMetaTag('og:image', image, 'property');
  setMetaTag('og:locale', locale, 'property');

  // Twitter
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:url', fullUrl, 'name');
  setMetaTag('twitter:title', title || 'Agroland.kz', 'name');
  setMetaTag('twitter:description', description || 'Ауыл шаруашылығы тауарларының нарығы', 'name');
  setMetaTag('twitter:image', image, 'name');

  // Structured Data
  if (structuredData) {
    setStructuredData(structuredData, 'page');
  }
}

/**
 * Product Schema.org деректерін құру
 * @param {Object} product - Өнім деректері
 * @returns {Object} Structured data объектісі
 */
export function createProductSchema(product) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
  };

  if (product.description) {
    schema.description = product.description;
  }

  const images = Array.isArray(product.images)
    ? product.images.filter(Boolean)
    : (product.images ? [product.images] : []);
  if (images.length > 0) {
    schema.image = images;
  }

  if (product.sku) schema.sku = product.sku;
  if (product.brand) schema.brand = { "@type": "Brand", "name": product.brand };

  const offers = { "@type": "Offer" };
  let hasOffer = false;

  if (product.price != null && !Number.isNaN(Number(product.price))) {
    offers.price = String(product.price);
    offers.priceCurrency = product.currency || "KZT";
    hasOffer = true;
  } else if (product.priceMin != null || product.priceMax != null) {
    offers.priceSpecification = {
      "@type": "PriceSpecification",
      ...(product.priceMin != null ? { minPrice: Number(product.priceMin) } : {}),
      ...(product.priceMax != null ? { maxPrice: Number(product.priceMax) } : {}),
      priceCurrency: product.currency || "KZT",
    };
    hasOffer = true;
  }

  if (hasOffer) {
    offers.availability = product.inStock
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock";
    offers.seller = {
      "@type": "Organization",
      "name": product.sellerName || "Agroland.kz",
    };
    offers.url = product.canonical || `${BASE_URL}${window.location.pathname}`;
    schema.offers = offers;
  }

  if (product.rating && product.rating.value != null) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(product.rating.value),
      reviewCount: Number(product.rating.count) || 1,
    };
  }

  return schema;
}

/**
 * Article Schema.org деректерін құру (жаңалықтар үшін)
 * @param {Object} article - Мақала деректері
 * @returns {Object} Structured data объектісі
 */
export function createArticleSchema(article) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "author": {
      "@type": "Organization",
      "name": "Agroland.kz"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Agroland.kz",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo512.png`
      }
    }
  };

  if (article.description) schema.description = article.description;
  if (article.image) schema.image = article.image;
  if (article.datePublished) schema.datePublished = article.datePublished;
  if (article.dateModified) schema.dateModified = article.dateModified;

  return schema;
}

/**
 * BreadcrumbList Schema.org деректерін құру
 * @param {Array} items - Breadcrumb элементтері ({name, url})
 * @returns {Object} Structured data объектісі
 */
export function createBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => {
      const listItem = {
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
      };
      if (item.url) listItem.item = item.url;
      return listItem;
    })
  };
}

/**
 * ItemList Schema.org деректерін құру (категория беттері үшін)
 * @param {string} name - Категория атауы
 * @param {Array} items - Элементтер тізімі ({slug, id} объектілері)
 * @returns {Object} Structured data объектісі
 */
export function createItemListSchema(name, items) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${name} — Agroland.kz`,
    "numberOfItems": items.length,
    "itemListElement": items.slice(0, 20).map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": `${BASE_URL}/announcement/${item.slug || generateSlug(item.title, item.location, item.id)}`
    }))
  };
}

const seo = {
  setTitle,
  setSEO,
  setStructuredData,
  removeStructuredData,
  setHTMLLang,
  createProductSchema,
  createArticleSchema,
  createBreadcrumbSchema,
  createItemListSchema
};

export default seo;